import {useMemo} from "react";
import {
    OrderFilter,
    OrderOverviewRowDTO,
    OrderSearchParamsDTO,
    OrderSortField,
    OrderTableViewStateDTO
} from "@/api/data-contracts";
import {Constants} from "@/api/constants";
import {orderApi, tableViewStateApi} from "@/boxhub-api";
import {initialOrderStatuses} from "@/datacaches";
import {
    ORDER_SIZE_OPTIONS,
    OverviewSearchResult,
    OverviewState,
    OverviewStateAdapter,
    SortDirection
} from "@/common/overview-state/overview-state-model";
import {
    orderDocumentToFilters,
    OrderOverviewFilters,
    orderStateToDocument,
    orderStateToSearchParams
} from "@/common/overview-state/order-view-state";
import {useOverviewTableState} from "@/common/overview-state/useOverviewTableState";

/** Return shape of `useOrderOverviewState` — forwards the generic hook, plus paging convenience fields. */
export interface UseOrderOverviewStateResult {
    readonly applied: OverviewState<OrderOverviewFilters, OrderSortField>;
    readonly rows: OrderOverviewRowDTO[];
    readonly totalElements: number;
    readonly totalPages: number;
    readonly totalUnreadMessages: number;
    readonly page: number;
    readonly size: number;
    readonly defaults: OrderOverviewFilters;
    readonly defaultSize: number;
    /** Page-size choices for the size selector (Phase 1.3) — `Constants.ORDER_SEARCH_SIZE_OPTIONS`. */
    readonly sizeOptions: readonly number[];
    resetAll(): void;
    setSearchText(text: string): void;
    setSort(field: OrderSortField | undefined, direction: SortDirection | undefined): void;
    setSize(size: number): void;
    setPage(page: number): void;
    /** Sofortwirkung (Phase 2.2, Standing constraint 4): merges into `applied.filters` immediately. */
    setFilters(partial: Partial<OrderOverviewFilters>): void;
}

/**
 * Reads the stored view state of one order-search variant. Resolves to `undefined` on the 204-empty-body
 * case: Axios represents a bodyless 204 as an empty string in `response.data`, so a truthiness check on
 * the document alone is not enough (see the plan's "Standing constraints"; pattern mirrored from
 * `useRequestOverviewState.ts`).
 */
async function loadOrderDocument(variant: OrderFilter): Promise<OrderTableViewStateDTO | undefined> {
    const response = await tableViewStateApi.getOrderState(variant);
    const raw: unknown = response.data;
    if (response.status === 204 || raw === "" || raw == null) {
        return undefined;
    }
    return raw as OrderTableViewStateDTO;
}

/** "Everything selected" — the same default order-status selection for every variant. */
function buildDefaultFilters(): OrderOverviewFilters {
    return {orderStatuses: [...initialOrderStatuses]};
}

/**
 * Runs the order search and maps the response onto `OverviewSearchResult`: rows fall back to an empty
 * list, `page`/`size` fall back to the request parameters, and `totalElements`/`totalPages`/
 * `totalUnreadMessages` fall back to 0 — all for a response that omits them.
 */
async function searchOrderRows(
    params: OrderSearchParamsDTO
): Promise<OverviewSearchResult<OrderOverviewRowDTO>> {
    const response = await orderApi.searchOrders(params);
    const data = response.data;
    return {
        rows: data.rows ?? [],
        page: data.page ?? params.page ?? 0,
        size: data.size ?? params.size ?? Constants.ORDER_SEARCH_SIZE_DEFAULT,
        totalElements: data.totalElements ?? 0,
        totalPages: data.totalPages ?? 0,
        totalUnreadMessages: data.totalUnreadMessages ?? 0
    };
}

/**
 * Order-search adapter for `useOverviewTableState` (Phase 1.3), parametrized over one `OrderFilter`
 * variant (INTERNAL, EXTERNAL or NEUTRAL). Carries all variant-specific knowledge — which four endpoints
 * back the variant, what "everything selected" means, how the document and search parameters map onto the
 * generic state (`order-view-state.ts`) — and contains no state logic of its own (no draft/applied split,
 * no debouncing, no page clamping, no error handling): all of that lives in the generic hook.
 *
 * `PUT`/`GET`/`DELETE` on `orders/NEUTRAL` are reserved for the Innendienst and answer with 403 otherwise
 * (`TableViewStateService#requireAccessTo`); the hook's error path already handles that, no dedicated
 * carve-out is needed here.
 */
export function useOrderOverviewState(variant: OrderFilter): UseOrderOverviewStateResult {
    const adapter = useMemo<
        OverviewStateAdapter<
            OrderTableViewStateDTO,
            OrderOverviewRowDTO,
            OrderOverviewFilters,
            OrderSearchParamsDTO,
            OrderSortField
        >
    >(
        () => ({
            loadDocument: () => loadOrderDocument(variant),
            saveDocument: async (doc) => {
                await tableViewStateApi.putOrderState(variant, doc);
            },
            clearDocument: async () => {
                await tableViewStateApi.deleteOrderState(variant);
            },
            search: searchOrderRows,
            defaults: buildDefaultFilters,
            defaultSize: () => Constants.ORDER_SEARCH_SIZE_DEFAULT,
            toDocument: orderStateToDocument,
            fromDocument: (doc, defaults) => orderDocumentToFilters(doc, defaults),
            toSearchParams: (state) => orderStateToSearchParams(state, variant)
        }),
        [variant]
    );

    const state = useOverviewTableState(adapter);

    // `state` is already referentially stable (memoized in `useOverviewTableState`); spreading it into a
    // plain object literal on every call would throw that stability away again, so the paging convenience
    // fields are added inside a `useMemo` keyed on `state` itself instead.
    return useMemo(
        () => ({
            ...state,
            page: state.applied.page,
            size: state.applied.size,
            sizeOptions: ORDER_SIZE_OPTIONS
        }),
        [state]
    );
}
